import type { Hand } from '../../types/problems';

interface PlayableHandDisplayProps {
  hand: Hand;
  playedCards: string[];
  label: string;  // e.g. "You hold (West)", "Dummy (East)"
  /** When true, the outer wrapper stretches to fill its parent width. */
  stretch?: boolean;
  /** When true, played cards are removed from display entirely (not greyed out). */
  removePlayedCards?: boolean;
}

const SUIT_CONFIG = [
  { key: 'spades' as const, symbol: '\u2660', color: 'text-black', name: 'Spades', suitChar: 'S' },
  { key: 'hearts' as const, symbol: '\u2665', color: 'text-red-600', name: 'Hearts', suitChar: 'H' },
  { key: 'diamonds' as const, symbol: '\u2666', color: 'text-red-600', name: 'Diamonds', suitChar: 'D' },
  { key: 'clubs' as const, symbol: '\u2663', color: 'text-black', name: 'Clubs', suitChar: 'C' },
];

export function PlayableHandDisplay({ hand, playedCards, label, stretch, removePlayedCards }: PlayableHandDisplayProps) {
  const playedSet = new Set(playedCards.map(c => c.toUpperCase()));

  return (
    <div className={stretch ? 'w-full min-w-[10rem]' : 'min-w-[10rem]'}>
      {/* Hand card area — label inside the container like Problems page */}
      <div className="bg-cream border border-line rounded px-3 py-2">
        {/* Feature 028: floor raised 0.7rem → 0.75rem. At 0.7rem this bottomed out at 11.2px,
            under the constitution VI text-size floor — and this label is what tells the learner
            WHOSE hand they are looking at ("You hold (North)" / "Dummy (South)"), which is not
            something to make them squint at. Shared component, so the editor pages gain it too. */}
        <div className="font-semibold text-ink mb-1" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.9rem)' }}>
          {label}
        </div>
        {/* `counting-hand-suits` is a stable hook for index.css, which lays the four suits out
            as a 2x2 grid for the North/South hands on the rebuilt Counting page — stacked above
            and below the played trick, a four-row hand is twice the height the layout can spend.
            No rule targets the class by default, so every existing consumer is unaffected. */}
        <div className="counting-hand-suits space-y-0">
          {SUIT_CONFIG.map((suit) => {
            const cards = hand[suit.key] || '';
            // Filter out played cards when removePlayedCards is true
            const visibleRanks = removePlayedCards
              ? cards.split('').filter(rank => {
                  const cardCode = `${suit.suitChar}${rank}`.toUpperCase();
                  return !playedSet.has(cardCode);
                })
              : cards.split('');
            return (
              <div key={suit.key} className="py-0 leading-tight">
                <span
                  className={`${suit.color} inline-block align-middle text-center font-suits text-[1.5rem]`}
                  style={{ width: '1em' }}
                  title={suit.name}
                >
                  {suit.symbol}
                </span>
                <span className="align-middle" style={{ marginLeft: '0.1em' }}>
                  {removePlayedCards
                    ? visibleRanks.map((rank, idx) => (
                        <span
                          key={idx}
                          className="font-semibold tracking-wide text-base text-ink"
                        >
                          {rank}
                        </span>
                      ))
                    : cards.split('').map((rank, idx) => {
                        const cardCode = `${suit.suitChar}${rank}`.toUpperCase();
                        const isPlayed = playedSet.has(cardCode);
                        return (
                          <span
                            key={idx}
                            className={`font-semibold tracking-wide text-base ${
                              isPlayed ? 'text-[#c9c1ab]' : 'text-ink'
                            }`}
                          >
                            {rank}
                          </span>
                        );
                      })
                  }
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
